import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Box, Button, createStyles, Group, Title, Tooltip} from '@mantine/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {getLocationPathname} from 'BenchlingUtil';
import EntryCalendar from 'calendar/EntryCalendar';
import {HOMEPAGE_ANALYTICS_ACTIONS} from 'constants/analytics-products/HomepageAnalyticsConstants';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import EntityStore from 'stores/EntityStore';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import HomepageAdminBanner from './homepage/HomepageAdminBanner';
import {COLUMN_SPACING, HOMEPAGE_PATH, MAX_CONTENT_WIDTH} from './homepage/HomepageConstants';
import HomepageSearchButton from './homepage/HomepageSearchButton';
import {shouldShowAdminBanner, shouldShowWhatsNewButton} from './homepage/HomepageVisibility';
import HomepageCardGrid from './homepage/layout/HomepageCardGrid';
import useHomepageLayout from './homepage/layout/useHomepageLayout';
import OrganizationInviteBanner from './homepage/OrganizationInviteBanner';
import WhatsNewButton from './homepage/WhatsNewButton';

const useStyles = createStyles((theme, {showHeaderShadow}: {showHeaderShadow: boolean}) => ({
  cardsContainerWrapper: {
    flexGrow: 1,
    padding: `${theme.spacing.lg} ${COLUMN_SPACING}`,
    backgroundColor: theme.colors.gray[0],
  },
  closeIcon: {
    color: theme.colors.gray[6],
    '&&': {
      height: 18,
      width: 18,
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  headerActions: {
    gridColumn: 3,
    justifySelf: 'end',
  },
  stickyHeader: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    alignItems: 'center',
    padding: theme.spacing.xxl,
    backgroundColor: theme.white,
    borderBottom: `2px solid ${theme.colors.gray[0]}`,
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    transition: 'box-shadow 0.25s ease-in-out',
    boxShadow: showHeaderShadow ? '0 2px 12px rgba(0, 0, 0, 0.1)' : 'none',
  },
}));

/** Full screen homepage with various sections. */
export default function Homepage(): JSX.Element {
  const {homepageEnableCustomizePanels} = useRolloutFlags();
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const {
    currentLayouts,
    editingLayout,
    isSavingLayout,
    handleDiscardLayout,
    handleEditLayout,
    handleLayoutsChange,
    handleSaveLayout,
  } = useHomepageLayout();
  const calendarButtonLabel = showCalendar ? 'Back to home' : 'My Calendar';

  const user = useStoreSelector(
    EntityStore,
    useCallback((store) => store.currentUser(), [])
  );

  useOnMount(() => {
    // The redesigned homepage only supports fully expanded or closed — never semi-expanded.
    if (HOMEPAGE_PATH === getLocationPathname()) {
      SidepanelActions.expand();
    } else {
      SidepanelActions.close({autoOpenFile: false});
    }
  });

  const [showHeaderShadow, setShowHeaderShadow] = useState(false);
  const scrollableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollableRef.current == null) {
      return;
    }
    const scrollable = scrollableRef.current;
    const handleScroll = (): void => {
      setShowHeaderShadow(scrollable.scrollTop > 0);
    };
    scrollable.addEventListener('scroll', handleScroll);
    return () => {
      scrollable.removeEventListener('scroll', handleScroll);
    };
  }, [scrollableRef]);

  const {classes, cx} = useStyles({showHeaderShadow});

  return (
    <div
      ref={scrollableRef}
      className={cx(classes.container, 'sidepanel')}
      {...getTestProps({component: 'Homepage', element: 'root'})}
    >
      <Box component="header" className={classes.stickyHeader}>
        <Group spacing="xl">
          <Title order={3}>Welcome back{user != null ? `, ${user.name}` : ''}</Title>
          {!editingLayout && (
            <Tooltip label={calendarButtonLabel} openDelay={500}>
              <ActionIcon
                size="md"
                radius="md"
                // eslint-disable-next-line no-restricted-syntax -- no custom variant matches the design, yet
                variant="light"
                aria-label={calendarButtonLabel}
                onClick={() => {
                  if (!showCalendar) {
                    trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.CLICKED_CALENDAR_BUTTON);
                  }
                  setShowCalendar(!showCalendar);
                }}
                {...getTestProps({component: 'Homepage', element: 'calendarButton'})}
              >
                <BenchlingIcon icon={showCalendar ? 'home-dashboard' : 'calendar'} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
        <HomepageSearchButton />
        <Group spacing="xl" className={classes.headerActions}>
          {shouldShowWhatsNewButton() && !editingLayout && <WhatsNewButton />}
          {!showCalendar &&
            (editingLayout ? (
              <>
                <Button
                  color="green"
                  size="sm"
                  loading={isSavingLayout}
                  onClick={handleSaveLayout}
                  {...getTestProps({component: 'Homepage', element: 'saveLayoutButton'})}
                >
                  Save layout
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isSavingLayout}
                  onClick={handleDiscardLayout}
                  {...getTestProps({component: 'Homepage', element: 'discardLayoutButton'})}
                >
                  Discard
                </Button>
              </>
            ) : (
              homepageEnableCustomizePanels && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEditLayout}
                  {...getTestProps({component: 'Homepage', element: 'editLayoutButton'})}
                >
                  Edit layout
                </Button>
              )
            ))}
          <ActionIcon
            onClick={() => {
              trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.CLICKED_CLOSE_BUTTON);
              SidepanelActions.close();
            }}
            {...getTestProps({component: 'Homepage', element: 'closeButton'})}
          >
            <BenchlingIcon icon="close" altText="Close" className={classes.closeIcon} />
          </ActionIcon>
        </Group>
      </Box>
      {/* Inexplicably, EntryCalendar has only left and top margin */}
      {showCalendar ? (
        <div className="u-spacing-right" data-amp-mask={true}>
          <EntryCalendar />
        </div>
      ) : (
        <div className={classes.cardsContainerWrapper}>
          <Box maw={`${MAX_CONTENT_WIDTH}px`} m="0 auto">
            {shouldShowAdminBanner() && <HomepageAdminBanner />}
            <OrganizationInviteBanner />
            <HomepageCardGrid
              editingLayout={editingLayout}
              layouts={currentLayouts}
              onLayoutsChange={handleLayoutsChange}
            />
          </Box>
        </div>
      )}
    </div>
  );
}
